import { getInitials } from '../lib/initials';
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { getEventById, deleteEvent, getUserRsvp, adminSetRsvp, adminRemoveRsvp } from '../services/events';
import { getAllUsers } from '../services/users';
import { getFeed } from '../services/posts';
import { useAuth } from '../hooks/useAuth';
import RsvpButtons from '../components/events/RsvpButtons';
import { PostComposer } from '../components/posts/PostComposer';
import { PostCard } from '../components/posts/PostCard';

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showComposer, setShowComposer] = useState(false);

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => getEventById(eventId!),
    enabled: !!eventId,
  });

  const { data: userRsvp } = useQuery({
    queryKey: ['user-rsvp', eventId],
    queryFn: () => getUserRsvp(eventId!),
    enabled: !!eventId,
  });

  // Fetch event posts
  const {
    data: postsData,
    isLoading: postsLoading,
  } = useInfiniteQuery({
    queryKey: ['posts', 'event', eventId],
    queryFn: ({ pageParam = 1 }) => getFeed(pageParam, 20, eventId),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasMore
        ? lastPage.pagination.page + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: !!eventId,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getAllUsers,
    enabled: Boolean(user?.isAdmin) || Boolean(event && (
      event.organizers?.some((o) => o.user?.id === user?.id) ||
      event.organizerId === user?.id
    )),
  });

  const adminRsvpMutation = useMutation<void, Error, { targetUserId: string; status: string | null }>({
    mutationFn: async ({ targetUserId, status }) => {
      if (status) {
        await adminSetRsvp(eventId!, targetUserId, status as any);
      } else {
        await adminRemoveRsvp(eventId!, targetUserId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteEvent(eventId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate('/events');
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-300 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          Kunne ikke indlæse begivenhed
        </div>
      </div>
    );
  }

  const startDate = new Date(event.startTime);
  const organizerUsers = (event.organizers && event.organizers.length > 0
    ? event.organizers.map((o) => o.user)
    : (event.organizer ? [event.organizer] : [])).filter(Boolean);
  const isOrganizer = organizerUsers.some((organizer) => organizer.id === user?.id)
    || (event.organizerId != null && event.organizerId === user?.id);
  const canManageEvent = isOrganizer || Boolean(user?.isAdmin);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('da-DK', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('da-DK', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const attendingCount = event.attendees.filter((a) => a.status === 'attending').length;
  const maybeCount = event.attendees.filter((a) => a.status === 'maybe').length;
  const notAttendingCount = event.attendees.filter((a) => a.status === 'not_attending').length;

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Back button */}
      <Link
        to="/events"
        className="inline-flex items-center text-blue-500 hover:text-blue-700 mb-4"
      >
        <svg
          className="w-5 h-5 mr-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Tilbage
      </Link>

      {/* Event image */}
      {event.imageUrl && (
        <img
          src={event.imageUrl}
          alt={event.title}
          className="w-full h-64 object-cover rounded-lg mb-6"
        />
      )}

      {/* Event header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-3xl font-bold text-gray-900">{event.title}</h1>
          
          {canManageEvent && (
            <div className="flex gap-2">
              <Link
                to={`/events/${eventId}/edit`}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Rediger
              </Link>
              <button
                onClick={() => {
                  if (confirm('Er du sikker på at du vil slette denne begivenhed?')) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                Slet
              </button>
            </div>
          )}
        </div>

        {/* Date and time */}
        <div className="flex items-center gap-2 text-lg text-gray-700 mb-3">
          <svg
            className="w-6 h-6 text-orange"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="font-medium">{formatDate(startDate)}</span>
        </div>

        <div className="flex items-center gap-2 text-lg text-gray-700 mb-3">
          <svg
            className="w-6 h-6 text-orange"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>
            {formatTime(startDate)}
            {(() => {
              if (!event.endTime) return null;
              const endDate = new Date(event.endTime);
              const defaultEnd = new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
              if (Math.abs(endDate.getTime() - defaultEnd.getTime()) < 60000) return null;
              return <> &ndash; {formatTime(endDate)}</>;
            })()}
          </span>
        </div>
        {event.location && (
          <div className="flex items-center gap-2 text-lg text-gray-700 mb-3">
            <svg className="w-6 h-6 text-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{event.location}</span>
          </div>
        )}

        {/* Organizer — right after location */}
        <p className="text-sm text-gray-500 mb-4">
          Arrangeret af{' '}
          <span className="font-medium text-gray-700">
            {organizerUsers.length > 0
              ? organizerUsers.map((o) => o.name).join(', ')
              : 'Slettet bruger'}
          </span>
        </p>
        <p className="text-gray-700 whitespace-pre-wrap mb-6">{event.description}</p>

        {/* RSVP Buttons */}
        <div className="pt-4 border-t border-gray-200">
          <RsvpButtons eventId={eventId!} currentStatus={userRsvp?.status} />
        </div>
      </div>

      {/* Attendees */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Deltagere ({attendingCount + maybeCount + notAttendingCount})
        </h2>

        {attendingCount + maybeCount + notAttendingCount === 0 ? (
          <p className="text-gray-500">Ingen har tilmeldt sig endnu</p>
        ) : (
          <div className="space-y-6">
            {/* Attending */}
            {attendingCount > 0 && (
              <div>
                <h3 className="text-base font-semibold text-primary-600 mb-3">
                  Deltager ({attendingCount})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {event.attendees
                    .filter((a) => a.status === 'attending')
                    .map((attendee) => (
                      <Link
                        key={attendee.id}
                        to={`/profile/${attendee.user.id}`}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        {attendee.user.profileImageUrl ? (
                          <img
                            src={attendee.user.profileImageUrl}
                            alt={attendee.user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {getInitials(attendee.user.name)}
                            </span>
                          </div>
                        )}
                        <span className="text-sm font-medium">{attendee.user.name}</span>
                      </Link>
                    ))}
                </div>
              </div>
            )}

            {/* Maybe */}
            {maybeCount > 0 && (
              <div>
                <h3 className="text-base font-semibold text-yellow-600 mb-3">
                  Måske ({maybeCount})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {event.attendees
                    .filter((a) => a.status === 'maybe')
                    .map((attendee) => (
                      <Link
                        key={attendee.id}
                        to={`/profile/${attendee.user.id}`}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        {attendee.user.profileImageUrl ? (
                          <img
                            src={attendee.user.profileImageUrl}
                            alt={attendee.user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {getInitials(attendee.user.name)}
                            </span>
                          </div>
                        )}
                        <span className="text-sm font-medium">{attendee.user.name}</span>
                      </Link>
                    ))}
                </div>
              </div>
            )}

            {/* Not attending */}
            {notAttendingCount > 0 && (
              <div>
                <h3 className="text-base font-semibold text-gray-500 mb-3">
                  Afbud ({notAttendingCount})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {event.attendees
                    .filter((a) => a.status === 'not_attending')
                    .map((attendee) => (
                      <Link
                        key={attendee.id}
                        to={`/profile/${attendee.user.id}`}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        {attendee.user.profileImageUrl ? (
                          <img
                            src={attendee.user.profileImageUrl}
                            alt={attendee.user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {getInitials(attendee.user.name)}
                            </span>
                          </div>
                        )}
                        <span className="text-sm font-medium">{attendee.user.name}</span>
                      </Link>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Admin/organizer RSVP management */}
      {canManageEvent && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Administrer tilmeldinger</h2>
          <div className="space-y-2">
            {allUsers.map((u) => {
              const rsvp = event.attendees.find((a) => a.user.id === u.id);
              const status = rsvp?.status ?? null;
              return (
                <div key={u.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {u.profileImageUrl ? (
                      <img src={u.profileImageUrl} alt={u.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0">
                        {getInitials(u.name)}
                      </div>
                    )}
                    <span className="text-sm font-medium text-gray-800 truncate">{u.name}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => adminRsvpMutation.mutate({ targetUserId: u.id, status: status === 'attending' ? null : 'attending' })}
                      className={`px-2 py-1 text-xs rounded-lg border transition-colors ${
                        status === 'attending'
                          ? 'bg-green-500 text-white border-green-500'
                          : 'border-gray-300 text-gray-600 hover:bg-green-50'
                      }`}
                    >
                      Deltager
                    </button>
                    <button
                      onClick={() => adminRsvpMutation.mutate({ targetUserId: u.id, status: status === 'maybe' ? null : 'maybe' })}
                      className={`px-2 py-1 text-xs rounded-lg border transition-colors ${
                        status === 'maybe'
                          ? 'bg-yellow-400 text-white border-yellow-400'
                          : 'border-gray-300 text-gray-600 hover:bg-yellow-50'
                      }`}
                    >
                      Måske
                    </button>
                    <button
                      onClick={() => adminRsvpMutation.mutate({ targetUserId: u.id, status: status === 'not_attending' ? null : 'not_attending' })}
                      className={`px-2 py-1 text-xs rounded-lg border transition-colors ${
                        status === 'not_attending'
                          ? 'bg-red-500 text-white border-red-500'
                          : 'border-gray-300 text-gray-600 hover:bg-red-50'
                      }`}
                    >
                      Afbud
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments section */}
      <div className="bg-white rounded-lg shadow p-6 mt-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Kommentarer</h2>
          <button
            onClick={() => setShowComposer(!showComposer)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            {showComposer ? 'Luk' : 'Skriv kommentar'}
          </button>
        </div>

        {/* Post composer */}
        {showComposer && (
          <div className="mb-4">
            <PostComposer eventId={eventId} onPostCreated={() => setShowComposer(false)} />
          </div>
        )}

        {/* Posts list */}
        {postsLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        ) : (
          <>
            {postsData?.pages.flatMap((page) => page.posts).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">
                  Ingen kommentarer endnu. Vær den første til at skrive noget!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {postsData?.pages.flatMap((page) => page.posts).map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
